// eslint-disable-next-line no-restricted-imports
import API from 'app/gamma/src/api';
// eslint-disable-next-line no-restricted-imports
import type { TrelloClientQuery } from 'app/gamma/src/api/trello-client-js/trello-client-js.types';
// eslint-disable-next-line no-restricted-imports
import { MemberResponse } from 'app/gamma/src/types/responses';
// eslint-disable-next-line no-restricted-imports
import { StandardThunkAction, Dispatch } from 'app/gamma/src/types';
// eslint-disable-next-line no-restricted-imports
import { Action, actionCreator } from '@trello/redux';
// eslint-disable-next-line no-restricted-imports
import {
  MEMBER_FIELDS_MINIMAL,
  MEMBER_FIELDS_NON_PUBLIC,
} from 'app/gamma/src/modules/loaders/fields';
import { featureFlagClient } from '@trello/feature-flag-client';
import { client } from '@trello/graphql';
import {
  LoadMemberProfileDocument,
  LoadMemberProfileQuery,
  LoadMemberProfileQueryVariables,
} from './LoadMemberProfileQuery.generated';
import {
  LoadMemberNonPublicFieldsDocument,
  LoadMemberNonPublicFieldsQuery,
  LoadMemberNonPublicFieldsQueryVariables,
} from './LoadMemberNonPublicFieldsQuery.generated';

// Action types
export const LOAD_MEMBER_REQUEST = Symbol('loaders/LOAD_MEMBER_REQUEST');
export const LOAD_MEMBER_SUCCESS = Symbol('loaders/LOAD_MEMBER_SUCCESS');
export const LOAD_MEMBER_ERROR = Symbol('loaders/LOAD_MEMBER_ERROR');

export type LoadMemberRequestAction = Action<
  typeof LOAD_MEMBER_REQUEST,
  string
>;
export type LoadMemberSuccessAction = Action<
  typeof LOAD_MEMBER_SUCCESS,
  MemberResponse
>;
export type LoadMemberErrorAction = Action<typeof LOAD_MEMBER_ERROR, Error>;

// Action creators
const loadMemberRequest = actionCreator<LoadMemberRequestAction>(
  LOAD_MEMBER_REQUEST,
);
export const loadMemberSuccess = actionCreator<LoadMemberSuccessAction>(
  LOAD_MEMBER_SUCCESS,
);
const loadMemberError = actionCreator<LoadMemberErrorAction>(LOAD_MEMBER_ERROR);

const fetchMember = (
  idMemberOrUsername: string,
  query: TrelloClientQuery | undefined,
) => {
  return API.client.rest.get<MemberResponse>(`members/${idMemberOrUsername}`, {
    query,
    headers: { 'X-Trello-Operation-Name': 'fetchMember' },
  });
};

export const loadMemberProfile = (
  idMemberOrUsername: string,
): StandardThunkAction => {
  return async (dispatch: Dispatch) => {
    dispatch(loadMemberRequest(idMemberOrUsername));
    const useGraphQL = featureFlagClient.get(
      'fep.use-graphql-for-member-loader',
      false,
    );
    if (!useGraphQL) {
      try {
        const member = await fetchMember(idMemberOrUsername, {
          fields: MEMBER_FIELDS_MINIMAL,
          tokens: 'all',
          sessions: 'all',
          credentials: 'all',
          logins: true,
        });

        dispatch(loadMemberSuccess(member));
      } catch (err) {
        dispatch(loadMemberError(err));
      }
    } else {
      const { data, error } = await client.query<
        LoadMemberProfileQuery,
        LoadMemberProfileQueryVariables
      >({
        query: LoadMemberProfileDocument,
        variables: {
          id: idMemberOrUsername,
        },
        context: {
          operationName: 'LoadMemberProfile',
          document: LoadMemberProfileDocument,
        },
        // eslint-disable-next-line @trello/disallow-fetch-policies
        fetchPolicy: 'network-only',
      });
      if (error) {
        dispatch(loadMemberError(error));
      } else {
        const member = data.member;

        if (!member) {
          return;
        }

        dispatch(loadMemberSuccess(member));
      }
    }
  };
};

export const loadMemberNonPublicFields = (
  idMemberOrUsername: string,
): StandardThunkAction => {
  return async (dispatch: Dispatch) => {
    dispatch(loadMemberRequest(idMemberOrUsername));
    const useGraphQL = featureFlagClient.get(
      'fep.use-graphql-for-member-loader',
      false,
    );
    if (!useGraphQL) {
      try {
        const member = await fetchMember(idMemberOrUsername, {
          fields: MEMBER_FIELDS_NON_PUBLIC,
        });

        dispatch(loadMemberSuccess(member));
      } catch (err) {
        dispatch(loadMemberError(err));
      }
    } else {
      const { data, error } = await client.query<
        LoadMemberNonPublicFieldsQuery,
        LoadMemberNonPublicFieldsQueryVariables
      >({
        query: LoadMemberNonPublicFieldsDocument,
        variables: {
          id: idMemberOrUsername,
        },
        context: {
          operationName: 'LoadMemberNonPublicFields',
          document: LoadMemberNonPublicFieldsDocument,
        },
        // eslint-disable-next-line @trello/disallow-fetch-policies
        fetchPolicy: 'network-only',
      });
      if (error) {
        dispatch(loadMemberError(error));
      } else {
        const member = data.member;

        if (!member) {
          return;
        }

        dispatch(loadMemberSuccess(member));
      }
    }
  };
};
